import { useTranslation } from 'react-i18next';
import { PERMISSION_MODULES, moduleLabelKey, permissionLabelKey } from '@/shared/permissions/catalog';
import { PermissionSwitch } from './PermissionSwitch';

/**
 * Árbol de permisos por módulo (Etapa 9). Cada módulo tiene un toggle "todos" y un
 * switch por permiso. Editable o solo lectura (para ver un rol de sistema).
 */
export function PermissionTree({
  value,
  onChange,
  readOnly = false,
}: {
  value: string[];
  onChange?: (keys: string[]) => void;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const selected = new Set(value);

  const toggleKey = (key: string) => {
    if (readOnly || !onChange) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange([...next]);
  };

  const toggleModule = (keys: string[], allSelected: boolean) => {
    if (readOnly || !onChange) return;
    const next = new Set(selected);
    if (allSelected) keys.forEach((k) => next.delete(k));
    else keys.forEach((k) => next.add(k));
    onChange([...next]);
  };

  return (
    <div className="space-y-3">
      {PERMISSION_MODULES.map((module) => {
        const selectedCount = module.keys.filter((k) => selected.has(k)).length;
        const allSelected = selectedCount === module.keys.length;
        const someSelected = selectedCount > 0;
        return (
          <div key={module.id} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{t(moduleLabelKey(module.id))}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {selectedCount}/{module.keys.length}
                </p>
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => toggleModule(module.keys, allSelected)}
                  className={`min-h-8 shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition ${
                    allSelected
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {allSelected ? t('permissions.tree.none') : t('permissions.tree.all')}
                </button>
              )}
              {readOnly && someSelected && !allSelected && (
                <span className="text-xs text-gray-400">{t('permissions.tree.partial')}</span>
              )}
            </div>
            <div className="grid grid-cols-1 gap-1.5 px-2 pb-2 sm:grid-cols-2 sm:px-3 sm:pb-3">
              {module.keys.map((key) => {
                const on = selected.has(key);
                return (
                  <label
                    key={key}
                    className={`flex min-h-12 items-center justify-between gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                      on
                        ? 'bg-teal-50 dark:bg-teal-950/40'
                        : 'bg-gray-50 dark:bg-gray-900/40'
                    } ${readOnly ? 'cursor-default opacity-80' : 'cursor-pointer hover:bg-teal-50/70 dark:hover:bg-teal-950/25'}`}
                  >
                    <span className={on ? 'font-medium text-teal-900 dark:text-teal-100' : 'text-gray-800 dark:text-gray-200'}>
                      {t(permissionLabelKey(key))}
                    </span>
                    <span className="relative inline-flex shrink-0">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={readOnly}
                        onChange={() => toggleKey(key)}
                        className="sr-only"
                      />
                      <PermissionSwitch on={on} />
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
